import React, { useEffect, useRef, useState } from 'react';
import { useTicketsStore } from '../../../store/ticketsStore';
import {
  CreateTicketInput,
  TicketCategory,
  TicketPriority,
} from '../../../services/ticketsApi';
import { FORM_FOR_CATEGORY } from './ticketTypeForms';
import styles from './TicketDetailModal.module.css';

/**
 * "+ New Ticket" modal.
 *
 * The modal shell handles category selection and submission. The form body
 * is swapped out per category — each per-type form (in `ticketTypeForms.tsx`)
 * collects the right fields for that issue type and registers a builder
 * that, on submit, returns either a fully-typed `CreateTicketInput` or a
 * validation error string.
 *
 * If the calling page already knows the category (e.g. the Delivery Issues
 * page), it should pass `defaultCategory` and `lockCategory` so the user
 * can't accidentally switch types and lose the form state.
 */

interface Props {
  open: boolean;
  defaultCategory?: TicketCategory;
  /** Hide the category selector — useful when opened from a scoped page. */
  lockCategory?: boolean;
  onClose: () => void;
  onCreated?: (ticketId: number) => void;
}

const CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: 'customer', label: 'Customer Issue' },
  { value: 'delivery', label: 'Delivery Issue' },
  { value: 'payment', label: 'Payment Issue' },
  { value: 'inventory', label: 'Inventory Issue' },
  { value: 'compliance', label: 'Compliance Issue' },
  { value: 'returns', label: 'Return / Dispute' },
  { value: 'other', label: 'Other' },
];

export const CreateTicketModal: React.FC<Props> = ({
  open,
  defaultCategory,
  lockCategory,
  onClose,
  onCreated,
}) => {
  const create = useTicketsStore((s) => s.create);
  const saving = useTicketsStore((s) => s.saving);

  const [category, setCategory] = useState<TicketCategory>(defaultCategory ?? 'customer');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [error, setError] = useState<string | null>(null);

  // Each per-type form registers its builder via this ref. On submit we
  // call it to get either the typed input or a validation message.
  const builderRef = useRef<(() => CreateTicketInput | string) | null>(null);
  const buildPayload = (build: () => CreateTicketInput | string) => {
    builderRef.current = build;
  };

  // Reset on open/close.
  useEffect(() => {
    if (!open) {
      setCategory(defaultCategory ?? 'customer');
      setPriority('medium');
      setError(null);
      builderRef.current = null;
    } else if (defaultCategory) {
      setCategory(defaultCategory);
    }
  }, [open, defaultCategory]);

  if (!open) return null;

  const Form = FORM_FOR_CATEGORY[category];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!builderRef.current) {
      setError('Form not ready — please fill in the fields below.');
      return;
    }
    const result = builderRef.current();
    if (typeof result === 'string') {
      setError(result);
      return;
    }
    setError(null);
    try {
      const created = await create(result);
      onCreated?.(created.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={styles.modal}
        style={{ maxWidth: 620 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.ticketBadge}>New Ticket</span>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              {CATEGORIES.find((c) => c.value === category)?.label}
            </span>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.body} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!lockCategory && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>
              <span>Issue Type *</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TicketCategory)}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
          )}

          {error && (
            <p style={{ color: '#B91C1C', background: '#FEE2E2', padding: 8, borderRadius: 6, margin: 0, fontSize: 13 }}>
              {error}
            </p>
          )}

          {/* Per-type form. Each form registers its builder on mount via
              `buildPayload`, which the submit handler calls. */}
          <Form
            key={category}
            priority={priority}
            setPriority={setPriority}
            buildPayload={buildPayload}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#2563EB', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Creating…' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicketModal;
